import {useEffect, useState} from "react";
import {api} from "@/shared/api";
import type {FilterUsersResponse, User} from "@/shared/timr";

/**
 * A selectable user in the notice dialog's user combo box. Intentionally a thin
 * projection of {@link User} — only what the combo box needs to identify and render
 * an option — so consumers are not coupled to the full user model.
 */
export type UserOption = {
	id: number;
	fullname: string;
};

export interface NoticeUserOptions {
	/** The selectable users. Empty until the request resolves. */
	users: UserOption[];
	/** True while the initial request is in flight. */
	loading: boolean;
	/** True when the request failed. Aborts (e.g. on unmount) are not failures. */
	error: boolean;
}

/**
 * Loads the users selectable in the working-time-notice dialog.
 *
 * Owns the data concern — the request, abort-on-unmount, and loading/error state —
 * so the combo box component that consumes it stays purely presentational. This hook
 * is the seam intended to grow: loading "different kinds of user lists" should become
 * variants (or parameters) of this hook, leaving the rendering component untouched.
 *
 * The request runs once per mount. The dialog remounts on every open (via the
 * {@code key} on the dialog instance), so the list is naturally refreshed each time
 * the dialog opens without any explicit refetch logic here.
 */
export function useNoticeUserOptions(): NoticeUserOptions {
	const [users, setUsers] = useState<UserOption[]>([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(false);

	useEffect(() => {
		const request = api("reports/filterUsers.json")
			.get({
				showAllUsers: true,
				activeUsersOnly: true,
				hideCurrentUserIfGroupLeader: false
			})
			.done((data: FilterUsersResponse) => {
				setUsers((data?.users ?? []).map((u: User) => ({id: u.id, fullname: u.fullname})));
			})
			.fail((_jqXHR, textStatus) => {
				// An abort is the expected unmount path, not a real failure.
				if (textStatus !== "abort") {
					setError(true);
				}
			})
			.always(() => setLoading(false));

		return () => {
			// Abort the in-flight request when the dialog unmounts.
			request.abort();
		};
	}, []);

	return {users, loading, error};
}
